const TOKEN_KEY = "quotes_token";
const USER_KEY = "quotes_user";

export function getToken(): string | null {
	return localStorage.getItem(TOKEN_KEY);
}

export function getUser(): { id: string; email: string; name: string; role: string } | null {
	const raw = localStorage.getItem(USER_KEY);
	return raw ? JSON.parse(raw) : null;
}

export function setAuth(token: string, user: { id: string; email: string; name: string; role: string }) {
	localStorage.setItem(TOKEN_KEY, token);
	localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearAuth() {
	localStorage.removeItem(TOKEN_KEY);
	localStorage.removeItem(USER_KEY);
}

export function requireAuth(): string {
	const token = getToken();
	if (!token) {
		window.location.href = "/";
		throw new Error("Not authenticated");
	}
	return token;
}

async function request(method: string, path: string, body?: unknown): Promise<unknown> {
	const token = getToken();
	const headers: Record<string, string> = { "Content-Type": "application/json" };
	if (token) headers["Authorization"] = `Bearer ${token}`;

	let res: Response;
	try {
		res = await fetch(path, {
			method,
			headers,
			body: body ? JSON.stringify(body) : undefined,
		});
	} catch {
		throw new Error("Network error — check your connection and try again");
	}

	if (res.status === 401) {
		clearAuth();
		window.location.href = "/";
		throw new Error("Unauthorized");
	}

	const text = await res.text();
	let data: unknown;
	try {
		data = JSON.parse(text);
	} catch {
		throw new Error(res.status === 502 ? "Request timed out — try again" : `Server error (${res.status})`);
	}

	if (!res.ok) throw new Error((data as { error: string }).error || "Request failed");
	return data;
}

export const api = {
	// Auth
	login: (username: string, password: string) => request("POST", "/api/login", { username, password }),

	// Quotes
	listQuotes: (status?: string) => request("GET", `/api/quotes${status ? `?status=${status}` : ""}`),
	getQuote: (id: string) => request("GET", `/api/quotes/${id}`),
	createQuote: (data: Record<string, unknown>) => request("POST", "/api/quotes", data),
	updateQuote: (id: string, data: Record<string, unknown>) => request("PUT", `/api/quotes/${id}`, data),
	deleteQuote: (id: string) => request("DELETE", `/api/quotes/${id}`),
	duplicateQuote: (id: string) => request("POST", `/api/quotes/${id}/duplicate`),

	// Sections
	createSection: (quoteId: string, data: Record<string, unknown>) => request("POST", `/api/quotes/${quoteId}/sections`, data),
	updateSection: (id: string, data: Record<string, unknown>) => request("PUT", `/api/sections/${id}`, data),
	deleteSection: (id: string) => request("DELETE", `/api/sections/${id}`),
	reorderSections: (quoteId: string, sectionIds: string[]) => request("PUT", `/api/quotes/${quoteId}/sections/reorder`, { section_ids: sectionIds }),

	// Line Items
	createItem: (sectionId: string, data: Record<string, unknown>) => request("POST", `/api/sections/${sectionId}/items`, data),
	updateItem: (id: string, data: Record<string, unknown>) => request("PUT", `/api/items/${id}`, data),
	deleteItem: (id: string) => request("DELETE", `/api/items/${id}`),
	reorderItems: (sectionId: string, itemIds: string[]) => request("PUT", `/api/sections/${sectionId}/items/reorder`, { item_ids: itemIds }),

	// AI
	generateQuote: (quoteId: string, prompt: string) => request("POST", `/api/quotes/${quoteId}/generate`, { prompt }),
	chatQuote: (quoteId: string, message: string) => request("POST", `/api/quotes/${quoteId}/chat`, { message }),
	getConversation: (quoteId: string) => request("GET", `/api/quotes/${quoteId}/conversation`),

	// Snapshots
	listSnapshots: (quoteId: string) => request("GET", `/api/quotes/${quoteId}/snapshots`),
	restoreSnapshot: (quoteId: string, snapId: string) => request("POST", `/api/quotes/${quoteId}/snapshots/${snapId}/restore`),

	// Sharing
	shareQuote: (quoteId: string) => request("POST", `/api/quotes/${quoteId}/share`),
	revokeShare: (quoteId: string) => request("DELETE", `/api/quotes/${quoteId}/share`),

	// Client view (no auth)
	viewQuote: (token: string) => fetch(`/api/quotes/view/${token}`).then((r) => r.json()),
	approveQuote: (token: string, data: { approved_by: string; approved_title?: string; approved_notes?: string }) =>
		fetch(`/api/quotes/view/${token}/approve`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }).then((r) => r.json()),

	// Knowledge
	listKnowledge: () => request("GET", "/api/knowledge"),
	createKnowledge: (data: Record<string, unknown>) => request("POST", "/api/knowledge", data),
	updateKnowledge: (id: string, data: Record<string, unknown>) => request("PUT", `/api/knowledge/${id}`, data),
	deleteKnowledge: (id: string) => request("DELETE", `/api/knowledge/${id}`),

	// Settings
	getSettings: () => request("GET", "/api/settings"),
	updateSettings: (data: Record<string, unknown>) => request("PUT", "/api/settings", data),

	// Users
	listUsers: () => request("GET", "/api/users"),
	createUser: (data: Record<string, unknown>) => request("POST", "/api/users", data),
	updateUser: (id: string, data: Record<string, unknown>) => request("PUT", `/api/users/${id}`, data),
	deleteUser: (id: string) => request("DELETE", `/api/users/${id}`),
};
